import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const Container = styled(motion.div)`
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 100%);
  color: white;
  padding: 20px;
  padding-top: 80px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 40px;
  max-width: 800px;
  margin: 0 auto 40px auto;
`;

const BackButton = styled(motion.button)`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  padding: 12px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
  transition: all 0.3s ease;
  font-size: 18px;
  font-weight: bold;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
`;

const Content = styled.div`
  max-width: 800px;
  margin: 0 auto;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  padding: 40px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const Section = styled.div`
  margin-bottom: 30px;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #667eea;
  margin-bottom: 15px;
  border-bottom: 2px solid rgba(102, 126, 234, 0.3);
  padding-bottom: 10px;
`;

const Paragraph = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #e0e0e0;
  margin-bottom: 15px;
  text-align: justify;
`;

const List = styled.ul`
  margin: 15px 0;
  padding-left: 20px;
`;

const ListItem = styled.li`
  font-size: 1rem;
  line-height: 1.6;
  color: #e0e0e0;
  margin-bottom: 8px;
`;

const Highlight = styled.span`
  color: #667eea;
  font-weight: 600;
`;

const LastUpdated = styled.div`
  text-align: center;
  margin-top: 40px;
  padding: 20px;
  background: rgba(102, 126, 234, 0.1);
  border-radius: 10px;
  border: 1px solid rgba(102, 126, 234, 0.2);
`;

const LastUpdatedText = styled.p`
  color: #667eea;
  font-size: 0.9rem;
  margin: 0;
`;

const TermsOfUse = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Container
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Header>
        <BackButton
          onClick={handleBack}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          ←
        </BackButton>
        <Title>Terms of Use</Title>
      </Header>

      <Content>
        <Section>
          <SectionTitle>1. Acceptance of Terms</SectionTitle>
          <Paragraph>
            By accessing and using ChefKats.AI ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>2. Description of Service</SectionTitle>
          <Paragraph>
            ChefKats.AI is an AI-powered recipe generation service that provides personalized cooking suggestions, meal planning, and culinary guidance. The service includes:
          </Paragraph>
          <List>
            <ListItem>AI-generated recipe recommendations</ListItem>
            <ListItem>Personalized meal planning</ListItem>
            <ListItem>Ingredient analysis and suggestions</ListItem>
            <ListItem>Nutritional information and dietary guidance</ListItem>
            <ListItem>Recipe storage and organization</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>3. User Accounts</SectionTitle>
          <Paragraph>
            To access certain features of the Service, you may be required to create an account. You are responsible for:
          </Paragraph>
          <List>
            <ListItem>Maintaining the confidentiality of your account credentials</ListItem>
            <ListItem>All activities that occur under your account</ListItem>
            <ListItem>Providing accurate and complete information</ListItem>
            <ListItem>Notifying us immediately of any unauthorized use</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>4. Acceptable Use</SectionTitle>
          <Paragraph>
            You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree not to:
          </Paragraph>
          <List>
            <ListItem>Use the Service for any illegal or unauthorized purpose</ListItem>
            <ListItem>Attempt to gain unauthorized access to the Service</ListItem>
            <ListItem>Interfere with or disrupt the Service</ListItem>
            <ListItem>Share inappropriate or harmful content</ListItem>
            <ListItem>Violate any applicable laws or regulations</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>5. AI-Generated Content</SectionTitle>
          <Paragraph>
            The Service provides AI-generated recipe suggestions and cooking advice. While we strive for accuracy, you acknowledge that:
          </Paragraph>
          <List>
            <ListItem>AI-generated content may contain errors or inaccuracies</ListItem>
            <ListItem>You should use your judgment when following recipes</ListItem>
            <ListItem>We are not responsible for any cooking outcomes</ListItem>
            <ListItem>Always follow proper food safety guidelines</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>6. Privacy and Data</SectionTitle>
          <Paragraph>
            Your privacy is important to us. Please review our <Highlight>Privacy Policy</Highlight>, which also governs your use of the Service, to understand our practices regarding the collection and use of your information.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>7. Subscription and Payment</SectionTitle>
          <Paragraph>
            Some features of the Service may require a subscription. By subscribing, you agree to:
          </Paragraph>
          <List>
            <ListItem>Pay all applicable fees and taxes</ListItem>
            <ListItem>Provide accurate billing information</ListItem>
            <ListItem>Authorize recurring charges for subscription renewals</ListItem>
            <ListItem>Cancel your subscription before the next billing cycle</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>8. Intellectual Property</SectionTitle>
          <Paragraph>
            The Service and its original content, features, and functionality are owned by ChefKats.AI and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>9. Disclaimers</SectionTitle>
          <Paragraph>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND. WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>10. Limitation of Liability</SectionTitle>
          <Paragraph>
            IN NO EVENT SHALL CHEFKATS.AI BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>11. Changes to Terms</SectionTitle>
          <Paragraph>
            We reserve the right to modify these Terms at any time. We will notify users of any material changes by posting the new Terms on this page. Your continued use of the Service after such modifications constitutes your acceptance of the updated Terms.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>12. Contact Information</SectionTitle>
          <Paragraph>
            If you have any questions about these Terms of Use, please contact us at:
          </Paragraph>
          <Paragraph>
            <Highlight>Email:</Highlight> legal@chefkats.ai<br />
            <Highlight>Address:</Highlight> ChefKats.AI Legal Department<br />
            <Highlight>Support:</Highlight> support@chefkats.ai
          </Paragraph>
        </Section>

        <LastUpdated>
          <LastUpdatedText>
            <strong>Last Updated:</strong> December 2024
          </LastUpdatedText>
        </LastUpdated>
      </Content>
    </Container>
  );
};

export default TermsOfUse;
